import React, { useState, useEffect } from "react";
import {
    TeamOutlined,
    ArrowLeftOutlined,
    HomeOutlined,
    AppstoreOutlined,
    StarFilled,
    CalendarOutlined,
    ClockCircleOutlined,
    UserOutlined,
    PhoneOutlined,
    CreditCardOutlined,
    EnvironmentOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import {
    Card,
    Typography,
    Avatar,
    Space,
    Empty,
    Collapse,
    Descriptions,
    ConfigProvider,
    Row,
    Col,
    Input,
    Select,
    DatePicker,
    Divider,
    Tag,
} from "antd";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { navigate } from "../components/Customs/navigation";
import { BooknLogService } from "../Services/BooknLogService";
import { DetailItem, Lightbox, LogStatusBadge, parseDocuments, SectionCard, SectionLabel, StatusBadge } from "../components/Rooms/history/History";
import { ENDPOINTS } from "../Services/ENDPOINT";
import { DEFAULT_CFG, LOG_STATUS, STATUS_CONFIG, T } from "./Styles/css";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { canViewModule } from "../../utils/permissionUtils";

const MODULE_KEY = "room_history_detail";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Text } = Typography;
const { Panel } = Collapse;

export default function RoomHistory() {
    const location = useLocation();
    const room = location.state?.room;
    const cfg = STATUS_CONFIG[room?.status?.toLowerCase()] || DEFAULT_CFG;
    const [searchText, setSearchText] = useState("");
    const [filterType, setFilterType] = useState("check_in"); // check_in | check_out
    const [dateFilter, setDateFilter] = useState("all"); // all | today | yesterday | range
    const [dateRange, setDateRange] = useState([]);

    let amenities = [];
    try { amenities = JSON.parse(room?.amenities || "[]"); } catch { }

    const [roomLog, setRoomLog] = useState([]);
    const [activeKey, setActiveKey] = useState(null);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {

        console.log("room",room);
        
        if (room?.id) getRoomLog(room.id);
    }, [room?.id]);

    const getRoomLog = async (id) => {
        try {
            const response = await BooknLogService.getRoomLog(id);
            setRoomLog(response.data);
        } catch (error) {
            console.error("Error fetching room log:", error);
        }
    };

    const isSameDay = (date1, date2) => {
        return dayjs(date1).format("YYYY-MM-DD") === dayjs(date2).format("YYYY-MM-DD");
    };

    const filteredLogs = roomLog.filter((log) => {
        const targetDate =
            filterType === "check_in" ? log.check_in : log.act_check_out_date;

        let matchDate = true;

        if (dateFilter === "today") {
            matchDate = isSameDay(targetDate, dayjs());
        } else if (dateFilter === "yesterday") {
            matchDate = isSameDay(targetDate, dayjs().subtract(1, "day"));
        } else if (dateFilter === "range" && dateRange.length === 2) {
            const d = dayjs(targetDate);

            matchDate =
                d.isSameOrAfter(dayjs(dateRange[0]).startOf("day")) &&
                d.isSameOrBefore(dayjs(dateRange[1]).endOf("day"));
        }

        // 🔍 Search filter (global)
        const search = searchText.toLowerCase();

        const matchSearch =
            log?.user_details?.guest_name?.toLowerCase().includes(search) ||
            log?.user_details?.phone?.includes(search) ||
            log?.user_details?.email?.toLowerCase().includes(search) ||
         log?.note?.toLowerCase().includes(search) ||
            String(log?.id || "").includes(search);

        return matchDate && matchSearch;
    });

    if (!canViewModule(MODULE_KEY)) {
        return <Empty description="No permission to view this page" />;
    }

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: T.accent,
                    borderRadius: 8,
                    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                    colorBorder: T.border,
                    colorBgContainer: T.surface,
                    colorText: T.text,
                    colorTextSecondary: T.textSub,
                    colorFillAlter: T.bg,
                    colorSplit: T.border,
                },
                components: {
                    Collapse: { headerBg: T.surface, contentBg: "#FAFBFD" },
                    Descriptions: { labelBg: T.bg },
                },
            }}
        >
            <div style={{ background: T.bg, minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
                <Card
                    style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, boxShadow: T.shadow }}
                    headStyle={{ borderBottom: `1px solid ${T.border}`, padding: "14px 24px" }}
                    title={
                        <div style={{ display: "flex", alignItems: "center", gap: 14, paddingTop: 4 }}>
                            <div
                                style={{
                                    width: 34, height: 34, borderRadius: "50%", background: T.accentLight,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", transition: "all 0.2s", color: T.accent,
                                    fontSize: 14, border: `1px solid ${T.accentSoft}`, flexShrink: 0,
                                }}
                                onClick={() => navigate(-1)}
                                onMouseEnter={e => { e.currentTarget.style.background = T.accentSoft; e.currentTarget.style.transform = "scale(1.06)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = T.accentLight; e.currentTarget.style.transform = "scale(1)"; }}
                            >
                                <ArrowLeftOutlined />
                            </div>
                            <div>
                                <div style={{ fontSize: 17, fontWeight: 700, color: T.text, letterSpacing: "-0.3px" }}>Room History</div>
                                <div style={{ fontSize: 14, color: T.textMuted, marginTop: 1 }}>Booking logs &amp; room details</div>
                            </div>
                        </div>
                    }
                >
                    <div style={{ display: "grid", gridTemplateColumns: "344px 1fr", gap: 20, alignItems: "start" }}>

                        {/* ══════ LEFT — Room Details ══════ */}
                        <div>
                            <SectionCard title="Room Details" icon={<HomeOutlined />}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                                    <div>
                                        <div style={{ fontSize: 20, fontWeight: 700, color: T.text, letterSpacing: "-0.5px" }}>{room?.room_name}</div>
                                        <div style={{ fontSize: 14, color: T.textMuted, marginTop: 2 }}>Room #{room?.room_number}</div>
                                    </div>
                                    <StatusBadge cfg={cfg} />
                                </div>

                                {room?.description && (
                                    <div
                                        style={{
                                            fontSize: 12.5, color: T.textSub, lineHeight: 1.65,
                                            padding: "10px 12px", background: T.bg,
                                            borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                                            marginBottom: 14, wordBreak: "break-word",
                                        }}
                                        dangerouslySetInnerHTML={{ __html: room.description }}
                                    />
                                )}

                                <div style={{ height: 1, background: T.border, margin: "14px 0" }} />

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 4 }}>
                                    <DetailItem icon={<AppstoreOutlined />} label="Room Type" value={room?.room_type} />
                                    <DetailItem icon={<TeamOutlined />} label="Max Occupancy" value={room?.max_occupancy ? `${room.max_occupancy} guests` : "—"} />
                                    <DetailItem icon={<HomeOutlined />} label="Floor" value={room?.floor} />
                                    <DetailItem icon={<StarFilled />} label="Status" value={room?.status} />
                                </div>

                                {amenities.length > 0 && (
                                    <>
                                        <div style={{ height: 1, background: T.border, margin: "14px 0" }} />
                                        <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                                            Amenities
                                        </div>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                            {amenities.map((a, i) => (
                                                <span key={i} style={{
                                                    fontSize: 14, padding: "3px 9px", borderRadius: 20,
                                                    background: T.accentLight, color: T.accent,
                                                    border: `1px solid ${T.accentSoft}`, fontWeight: 500,
                                                }}>
                                                    {a}
                                                </span>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </SectionCard>
                        </div>

                        {/* ══════ RIGHT — Room Log ══════ */}
                        <div>
                            <SectionCard
                                title="Room Log"
                                icon={<FileTextOutlined />}
                                extra={
                                    roomLog.length > 0 && (
                                        <span style={{
                                            fontSize: 14, fontWeight: 600, padding: "3px 10px",
                                            borderRadius: 20, background: T.accentLight, color: T.accent,
                                        }}>
                                            {roomLog.length} {roomLog.length === 1 ? "Entry" : "Entries"}
                                        </span>
                                    )
                                }
                            >
                                {roomLog.length === 0 ? (
                                    <Empty
                                        description={<span style={{ color: T.textMuted, fontSize: 15 }}>No log entries found</span>}
                                        style={{ padding: "48px 0" }}
                                    />
                                ) : (
                                    <>

                                        <Row gutter={10} style={{ marginBottom: 14 }}>
                                            {/* 🔍 Search */}
                                            <Col span={8}>
                                                <Input
                                                    placeholder="Search guest, phone, email..."
                                                    allowClear
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                />
                                            </Col>

                                            {/* 🔁 Check-in / Check-out */}
                                            <Col span={5}>
                                                <Select
                                                    allowClear={true}
                                                    placeholder="Filter by type (check-in/ out)"
                                                    value={filterType}
                                                    onChange={setFilterType}
                                                    style={{ width: "100%" }}
                                                    options={[
                                                        { label: "Check-in", value: "check_in" },
                                                        { label: "Check-out", value: "check_out" },
                                                    ]}
                                                />
                                            </Col>

                                            {/* 📅 Date Filter */}
                                            <Col span={5}>
                                                <Select
                                                    // allowClear={true}
                                                    value={dateFilter}
                                                    onChange={(val) => {
                                                        setDateFilter(val);
                                                        if (val !== "range") setDateRange([]);
                                                    }}
                                                    style={{ width: "100%" }}
                                                    options={[
                                                        { label: "All", value: "all" },
                                                        { label: "Today", value: "today" },
                                                        { label: "Yesterday", value: "yesterday" },
                                                        { label: "Custom Range", value: "range" },
                                                    ]}
                                                />
                                            </Col>

                                            {/* 📆 Date Range Picker */}
                                            {dateFilter === "range" && (
                                                <Col span={6}>
                                                    <DatePicker.RangePicker
                                                        style={{ width: "100%" }}
                                                        value={dateRange}
                                                        onChange={(dates) => setDateRange(dates || [])}
                                                    />
                                                </Col>
                                            )}
                                        </Row>

                                        <Divider />

                                        <Collapse
                                            accordion
                                            expandIconPosition="end"
                                            activeKey={activeKey}
                                            onChange={setActiveKey}
                                            style={{ background: "transparent", border: "none" }}
                                        >
                                            {filteredLogs.map((log) => {
                                                const u = log.user_details;
                                                const tagCfg = LOG_STATUS[log.status] || { bg: T.bg, text: T.textSub, dot: T.textMuted, label: log.status };
                                                const isActive = activeKey === String(log.id);
                                                const docs = parseDocuments(u?.document_upload);

                                                return (
                                                    <Panel
                                                        key={log.id}
                                                        style={{
                                                            background: T.surface,
                                                            border: `1px solid ${isActive ? `${T.accent}33` : T.border}`,
                                                            borderRadius: T.radiusSm,
                                                            marginBottom: 10,
                                                            overflow: "hidden",
                                                            transition: "box-shadow 0.2s, border-color 0.2s",
                                                            boxShadow: isActive ? T.shadowHover : "none",
                                                        }}
                                                        header={
                                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                                                                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 15 }}>
                                                                    <div style={{
                                                                        width: 24, height: 24, borderRadius: 6, background: T.accentLight,
                                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                                        color: T.accent, fontSize: 14,
                                                                    }}>
                                                                        <CalendarOutlined />
                                                                    </div>
                                                                    <span style={{ fontWeight: 600, color: T.text, fontSize: 15 }}>
                                                                        {/* {log.check_in} */}
                                                                        {dayjs(log.check_in).format("DD-MM-YYYY")}

                                                                    </span>
                                                                    <span style={{ color: T.textMuted, fontSize: 14 }}>
                                                                        <ClockCircleOutlined style={{ marginRight: 2 }} />{log.check_in_time}
                                                                    </span>
                                                                    <span style={{ color: T.textMuted, fontSize: 14, margin: "0 2px" }}>→</span>
                                                                    <span style={{ fontWeight: 600, color: T.text, fontSize: 15 }}>
                                                                        {log.act_check_out_date
                                                                            ? dayjs(log.act_check_out_date).format("DD-MM-YYYY")
                                                                            : "—"}
                                                                    </span>
                                                                    <span style={{ color: T.textMuted, fontSize: 14 }}>
                                                                        <ClockCircleOutlined style={{ marginRight: 2 }} />{log.act_check_out_time}
                                                                    </span>
                                                                </div>

                                                        <Tag color="blue">#{log.id}</Tag>
                                                                <Tag color={log.mode === "online" ? "volcano" : "default"}>
                                                                    {log.mode}
                                                                </Tag>

                                                                <LogStatusBadge cfg={tagCfg} />
                                                            </div>
                                                        }
                                                    >
                                                        {/* ── Booking Info ── */}
                                                        <div style={{ marginBottom: 18 }}>
                                                            <SectionLabel icon={<CalendarOutlined />}>Booking Information</SectionLabel>

                                                            <Descriptions
                                                                bordered size="small" column={2}
                                                                labelStyle={{ fontWeight: 600, fontSize: 14, color: T.textSub, background: T.bg, width: 160 }}
                                                                contentStyle={{ fontSize: 15, color: T.text }}
                                                            >
                                                                <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 5 }} />Check-in</>}>
                                                                    {/* {log.check_in} */}
                                                                    {dayjs(log.check_in).format("DD-MM-YYYY")}
                                                                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 14 }}>
                                                                        <ClockCircleOutlined style={{ marginRight: 3 }} />{log.check_in_time}
                                                                    </Text>
                                                                </Descriptions.Item>
                                                                <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 5 }} />Check-out</>}>
                                                                    {/* {log.check_out} */}
                                                                    {dayjs(log.check_out).format("DD-MM-YYYY")}
                                                                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 14 }}>
                                                                        <ClockCircleOutlined style={{ marginRight: 3 }} />{log.check_out_time}
                                                                    </Text>
                                                                </Descriptions.Item>
                                                                <Descriptions.Item label="Actual Check-out Date">
                                                                    {/* {log.act_check_out_date} */}
                                                                    {log.act_check_out_date
                                                                        ? dayjs(log.act_check_out_date).format("DD-MM-YYYY")
                                                                        : "—"}
                                                                </Descriptions.Item>
                                                                <Descriptions.Item label="Actual Check-out Time">{log.act_check_out_time}</Descriptions.Item>
                                                                <Descriptions.Item label="Time Diffrence"><strong>{log.difference_time}</strong></Descriptions.Item>
                                                                <Descriptions.Item label="Note" span={2}>
                                                                    <Text italic style={{ color: T.textMuted, fontSize: 12 }}>{log.note || "—"}</Text>
                                                                </Descriptions.Item>
                                                            </Descriptions>
                                                        </div>

                                                        {/* ── Guest Details ── */}
                                                        <SectionLabel icon={<UserOutlined />}>Guest Details</SectionLabel>
                                                        <Descriptions
                                                            bordered size="small" column={{ xs: 1, sm: 2 }}
                                                            labelStyle={{ fontWeight: 600, fontSize: 14, color: T.textSub, background: T.bg, width: 160 }}
                                                            contentStyle={{ fontSize: 15, color: T.text }}
                                                        >
                                                            <Descriptions.Item label="Guest Name">
                                                                <Space size={6}>
                                                                    <Avatar size={20} icon={<UserOutlined />} style={{ background: T.accentSoft, color: T.accent, fontSize: 10 }} />
                                                                    <Text strong style={{ fontSize: 15 }}>{u?.guest_name}</Text>
                                                                </Space>
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 5 }} />Phone</>}>
                                                                {u?.phone}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Email" span={2}>{u?.email}</Descriptions.Item>
                                                            <Descriptions.Item label="Guests">
                                                                {u?.guests}
                                                                {u?.extra_guests > 0 && (
                                                                    <Text type="secondary" style={{ marginLeft: 6, fontSize: 14 }}>
                                                                        (+{u.extra_guests} extra)
                                                                    </Text>
                                                                )}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Floor">{u?.floor}</Descriptions.Item>
                                                            <Descriptions.Item label="ID Type">
                                                                {u?.id_type} — <Text code style={{ fontSize: 14 }}>{u?.id_number}</Text>
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label={<><EnvironmentOutlined style={{ marginRight: 5 }} />Address</>}>
                                                                {u?.address}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Duration">{u?.duration_nights} night(s)</Descriptions.Item>
                                                            <Descriptions.Item label="Rate / Night">
                                                                <Text style={{ fontWeight: 600 }}>₹{u?.rate_per_night}</Text>
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Base Total">₹{u?.base_total}</Descriptions.Item>
                                                            <Descriptions.Item label="Extra Charges">₹{u?.extra_total}</Descriptions.Item>
                                                            <Descriptions.Item label="Total Amount">
                                                                <Text strong style={{ color: T.accent, fontSize: 14 }}>₹{u?.total_amount}</Text>
                                                            </Descriptions.Item>

                                                            {(() => {
                                                                const ps = u?.pricing_summary
                                                                    ? (typeof u.pricing_summary === "string"
                                                                        ? JSON.parse(u.pricing_summary)
                                                                        : u.pricing_summary)
                                                                    : {};
                                                                const overdue = Number(ps?.overdue_amount || 0);
                                                                if (overdue <= 0) return null;
                                                                return (
                                                                    <>
                                                                        <Descriptions.Item label="Overdue Charges">
                                                                            <Text strong style={{ color: "#cf1322", fontSize: 14 }}>
                                                                                ₹{overdue.toLocaleString()}
                                                                            </Text>
                                                                        </Descriptions.Item>
                                                                        <Descriptions.Item label="Total + Overdue">
                                                                            <Text strong style={{ color: "#cf1322", fontSize: 15 }}>
                                                                                ₹{(Number(u?.total_amount || 0) + overdue).toLocaleString()}
                                                                            </Text>
                                                                        </Descriptions.Item>
                                                                    </>
                                                                );
                                                            })()}
                                                      <Descriptions.Item label={<><CreditCardOutlined style={{ marginRight: 5 }} />Payment</>} span={2}>
                                                {(() => {
                                                    const ps = u?.pricing_summary
                                                        ? (typeof u.pricing_summary === "string" ? (() => { try { return JSON.parse(u.pricing_summary); } catch { return {}; } })() : u.pricing_summary)
                                                        : {};
                                                    const rows = Array.isArray(ps?.payment_rows) ? ps.payment_rows
                                                        : typeof ps?.payment_rows === "string"
                                                            ? (() => { try { return JSON.parse(ps.payment_rows); } catch { return []; } })()
                                                            : [];
                                                    if (rows.length > 0) {
                                                        return (
                                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                                                {rows.map((r, i) => (
                                                                    <span key={i} style={{
                                                                        fontSize: 13, fontWeight: 600, padding: "2px 9px",
                                                                        borderRadius: 20, background: T.accentLight, color: T.accent,
                                                                        border: `1px solid ${T.accentSoft}`,
                                                                    }}>
                                                                        {r.mode} — ₹{Number(r.amount || 0).toLocaleString()}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        );
                                                    }
                                                    return (
                                                        <span style={{
                                                            fontSize: 14, fontWeight: 600, padding: "2px 9px",
                                                            borderRadius: 20, background: T.accentLight, color: T.accent,
                                                            border: `1px solid ${T.accentSoft}`,
                                                        }}>
                                                            {u?.payment_mode}
                                                        </span>
                                                    );
                                                })()}
                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Special Requests" span={2}>
                                                                <Text style={{ color: T.textMuted, fontSize: 12 }}>{u?.special_requests || "—"}</Text>
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label="Last Updated" span={2}>
                                                                <Text type="secondary" style={{ fontSize: 14 }}>
                                                                    {
                                                                        u?.update_at
                                                                            ? dayjs(u.update_at).format("DD-MM-YYYY hh:mm A")
                                                                            : "—"
                                                                    }
                                                                </Text>
                                                            </Descriptions.Item>
                                                        </Descriptions>

                                                        {/* ── Documents ── */}
                                                        {docs.length > 0 && (
                                                            <div style={{ marginTop: 16 }}>
                                                                <SectionLabel icon={<FileTextOutlined />}>Uploaded Documents</SectionLabel>
                                                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                                                    {docs.map((path, i) => (
                                                                        <button
                                                                            key={i}
                                                                            onClick={() => setLightbox({ images: docs, startIndex: i })}
                                                                            style={{
                                                                                display: "inline-flex", alignItems: "center", gap: 6,
                                                                                fontSize: 14, padding: "5px 14px",
                                                                                background: T.accentLight, color: T.accent,
                                                                                border: `1px solid ${T.accentSoft}`,
                                                                                borderRadius: 20, cursor: "pointer", fontWeight: 500,
                                                                                transition: "background 0.2s",
                                                                            }}
                                                                            onMouseEnter={e => e.currentTarget.style.background = T.accentSoft}
                                                                            onMouseLeave={e => e.currentTarget.style.background = T.accentLight}
                                                                        >
                                                                            <FileTextOutlined style={{ fontSize: 12 }} />
                                                                            Document {i + 1}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Panel>
                                                );
                                            })}
                                        </Collapse>
                                    </>
                                )}
                            </SectionCard>
                        </div>
                    </div>
                </Card>

                {/* ── Lightbox ── */}
                {lightbox && (
                    <Lightbox
                        images={lightbox.images}
                        startIndex={lightbox.startIndex}
                        onClose={() => setLightbox(null)}
                    />
                )}
            </div>
        </ConfigProvider>
    );
}